import { z } from 'zod';
import { v4 } from 'uuid';

export const MealEntrySchema = z.object({
  recipeId: z.string().optional(),
  title: z.string().min(1),
  // Defaults to the recipe's servings when not set
  servings: z.number().int().positive().max(99).optional(),
});

export const SLOTS = ['breakfast', 'lunch', 'dinner'] as const;

export type Slot = (typeof SLOTS)[number];

export const DayMealsSchema = z.object({
  breakfast: MealEntrySchema.optional(),
  lunch: MealEntrySchema.optional(),
  dinner: MealEntrySchema.optional(),
});

// Before meal slots, a day held a single meal: read it as that day's dinner.
// The day is written back in the new shape the next time it changes.
const DaySchema = z.preprocess(
  (day) =>
    day && typeof day === 'object' && 'title' in day ? { dinner: day } : day,
  DayMealsSchema,
);

export const WeekPlanSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => v4()),
  user: z.string().optional(),
  weekStart: z.string(),
  meals: z.record(z.string(), DaySchema).default({}),
});

// Reads a stored plan without rejecting it over one odd field: only the
// meals are normalised, day by day, and a malformed day is left out
export function readWeekPlan(data: Record<string, unknown>): WeekPlan {
  const meals: Record<string, DayMeals> = {};
  const stored = (data.meals ?? {}) as Record<string, unknown>;
  for (const [day, value] of Object.entries(stored)) {
    const parsed = DaySchema.safeParse(value);
    if (parsed.success) {
      meals[day] = parsed.data;
    }
  }
  return { ...(data as WeekPlan), meals };
}

export type MealEntry = z.infer<typeof MealEntrySchema>;
export type DayMeals = z.infer<typeof DayMealsSchema>;
export type WeekPlan = z.infer<typeof WeekPlanSchema>;

export const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type Day = (typeof DAYS)[number];
